import React, { HTMLAttributes } from "react";

import { AutocompleteSelectInputProps } from "veneer/AutocompleteSelectInput";
import Box from "veneer/Box";
import Checkbox from "veneer/Checkbox";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import ListItemButton from "veneer/ListItemButton";

import type {
  DeviceContentSourceFilter,
  DeviceFilterId,
  DeviceMetricFilter,
  DeviceNetworkTypeFilter,
  FilterState,
  LastConnectedFilter,
} from "cortex/ui/src/fleet/view/device/model";
import {
  DataTableFilterContainer,
  DataTableMultiSelectFilter,
} from "cortex/ui/src/ui/filter/DataTableFilters";
import type { Option } from "cortex/ui/src/ui/table/types";

interface DeviceFiltersProps {
  osOptions?: ReadonlyArray<Option>;
  networkFilter?: boolean;
  networkOptions?: ReadonlyArray<Option>;
  onChange: (newValue: FilterState) => void;
  tagsOptions?: ReadonlyArray<Option>;
  selectedTagOptions?: ReadonlyArray<Option>;
  value: FilterState;
  versionOptions?: ReadonlyArray<Option>;
  visibleFilters: ReadonlyArray<DeviceFilterId>;
}

const CONNECTED_FILTER_OPTIONS: ReadonlyArray<LastConnectedFilter> = [
  "Active",
  "Hibernating",
];

const LAST_CONNECTED_OPTIONS: ReadonlyArray<LastConnectedFilter> = [
  ...CONNECTED_FILTER_OPTIONS,
  "Missed Check-in",
  "Offline",
  "Unreachable",
  "Never Connected",
];

const DEVICE_METRIC_OPTIONS: ReadonlyArray<DeviceMetricFilter> = [
  "Up to Date",
  "Pending Version",
  "Upgrade Available",
];

const CONTENT_SOURCE_OPTIONS: ReadonlyArray<DeviceContentSourceFilter> = [
  "Network Default",
  "Set on Device",
];

const NETWORK_TYPE_OPTIONS: ReadonlyArray<DeviceNetworkTypeFilter> = [
  "Production",
  "Sandbox",
];

const buildFilterState = (
  current: FilterState,
  patch: Partial<FilterState>,
): FilterState => ({
  contentSource: current.contentSource ?? [],
  lastConnected: current.lastConnected ?? [],
  metric: current.metric ?? null,
  network: current.network ?? null,
  networkType: current.networkType ?? [],
  os: current.os ?? [],
  version: current.version ?? null,
  tags: current.tags ?? [],
  visibleFilters: current.visibleFilters,
  ...patch,
});

const getSelectedFilterValues = (
  value?: ReadonlyArray<string> | string | null,
): ReadonlyArray<string> => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    return [value];
  }

  return [];
};

const getSelectedFilterOptions = (
  value: FilterState["network"] | FilterState["os"] | FilterState["version"],
  options?: ReadonlyArray<Option>,
): ReadonlyArray<Option> =>
  getSelectedFilterValues(value).map((selectedValue) => {
    const match = options?.find(
      (opt) => String(opt.value) === String(selectedValue),
    );

    return match ?? { label: selectedValue, value: selectedValue };
  });

const getSelectedLastConnectedOptions = (
  value?: FilterState["lastConnected"],
): Array<LastConnectedFilter> => {
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  if (selected.includes("Connected")) {
    return Array.from(
      new Set([
        ...selected.filter((option) => option !== "Connected"),
        ...CONNECTED_FILTER_OPTIONS,
      ]),
    );
  }

  return [...selected];
};

const getSelectedMetricOptions = (
  value?: FilterState["metric"],
): Array<DeviceMetricFilter> => {
  if (typeof value === "string") {
    return [value];
  }

  return value ? [...value] : [];
};

export default function DeviceTablesFilter({
  networkFilter,
  networkOptions,
  onChange,
  osOptions,
  tagsOptions,
  selectedTagOptions = [],
  value,
  versionOptions,
  visibleFilters,
}: DeviceFiltersProps) {
  const selectedNetworkOptions = getSelectedFilterOptions(
    value.network,
    networkOptions,
  );
  const selectedVersionOptions = getSelectedFilterOptions(
    value.version,
    versionOptions,
  );
  const selectedOsOptions = getSelectedFilterOptions(value.os, osOptions);
  const selectedLastConnectedOptions = getSelectedLastConnectedOptions(
    value.lastConnected,
  );
  const allConnectedSelected = CONNECTED_FILTER_OPTIONS.every((option) =>
    selectedLastConnectedOptions.includes(option),
  );
  const someConnectedSelected = CONNECTED_FILTER_OPTIONS.some((option) =>
    selectedLastConnectedOptions.includes(option),
  );

  type ConnectivityAutocompleteProps = AutocompleteSelectInputProps<
    LastConnectedFilter,
    true
  >;

  const renderConnectivityGroup: NonNullable<
    ConnectivityAutocompleteProps["renderGroup"]
  > = (params) => (
    <Box component="li" key={params.key}>
      {params.group === "Connected" && (
        <ListItemButton
          onClick={() => {
            const otherOptions = selectedLastConnectedOptions.filter(
              (option) => !CONNECTED_FILTER_OPTIONS.includes(option),
            );
            onChange(
              buildFilterState(value, {
                lastConnected: allConnectedSelected
                  ? otherOptions
                  : [...otherOptions, ...CONNECTED_FILTER_OPTIONS],
              }),
            );
          }}
        >
          <Checkbox
            checked={allConnectedSelected}
            indeterminate={someConnectedSelected && !allConnectedSelected}
            size="small"
          />
          Connected
        </ListItemButton>
      )}
      <List disablePadding>{params.children}</List>
    </Box>
  );

  const renderConnectivityOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: LastConnectedFilter,
    { selected }: { selected: boolean },
  ) => {
    const isConnectedChild = CONNECTED_FILTER_OPTIONS.includes(option);

    return (
      <ListItem {...props}>
        <Box alignItems="center" display="flex" pl={isConnectedChild ? 3 : 0}>
          <Checkbox checked={selected} size="small" />
          {option}
        </Box>
      </ListItem>
    );
  };

  return (
    <DataTableFilterContainer>
      {visibleFilters.includes("lastConnected") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-last-connected"
          groupBy={(option: LastConnectedFilter) =>
            CONNECTED_FILTER_OPTIONS.includes(option)
              ? "Connected"
              : "Connectivity"
          }
          label="Last Connected"
          onChange={(selected) =>
            onChange(buildFilterState(value, { lastConnected: [...selected] }))
          }
          options={LAST_CONNECTED_OPTIONS}
          renderGroup={renderConnectivityGroup}
          renderOption={renderConnectivityOption}
          value={selectedLastConnectedOptions}
        />
      )}
      {networkFilter && visibleFilters.includes("network") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-network"
          isOptionEqualToValue={(opt: Option, val: Option) =>
            String(opt.value) === String(val.value)
          }
          label="Network"
          onChange={(selected) =>
            onChange(
              buildFilterState(value, {
                network: selected.map((opt) => String(opt.value)),
              }),
            )
          }
          options={networkOptions || []}
          value={selectedNetworkOptions}
        />
      )}
      {visibleFilters.includes("version") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-player-version"
          isOptionEqualToValue={(opt: Option, val: Option) =>
            String(opt.value) === String(val.value)
          }
          label="Version"
          onChange={(selected) =>
            onChange(
              buildFilterState(value, {
                version: selected.map((opt) => String(opt.value)),
              }),
            )
          }
          options={versionOptions || []}
          value={selectedVersionOptions}
        />
      )}
      {visibleFilters.includes("metric") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-device-metrics"
          label="Version Status"
          onChange={(selected) =>
            onChange(buildFilterState(value, { metric: [...selected] }))
          }
          options={[...DEVICE_METRIC_OPTIONS]}
          value={getSelectedMetricOptions(value.metric)}
        />
      )}
      {visibleFilters.includes("tags") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-tags"
          isOptionEqualToValue={(opt: Option, val: Option) =>
            String(opt.value) === String(val.value)
          }
          label="Tags"
          onChange={(selected) =>
            onChange(
              buildFilterState(value, {
                tags: selected.map((opt) => String(opt.value)),
              }),
            )
          }
          options={tagsOptions || []}
          value={selectedTagOptions}
        />
      )}
      {visibleFilters.includes("os") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-os"
          isOptionEqualToValue={(opt: Option, val: Option) =>
            String(opt.value) === String(val.value)
          }
          label="OS"
          onChange={(selected) =>
            onChange(
              buildFilterState(value, {
                os: selected.map((opt) => String(opt.value)),
              }),
            )
          }
          options={osOptions || []}
          value={selectedOsOptions}
        />
      )}
      {visibleFilters.includes("contentSource") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-content-source"
          label="Content Source"
          onChange={(selected) =>
            onChange(buildFilterState(value, { contentSource: [...selected] }))
          }
          options={[...CONTENT_SOURCE_OPTIONS]}
          value={[...(value.contentSource ?? [])]}
        />
      )}
      {visibleFilters.includes("networkType") && (
        <DataTableMultiSelectFilter
          dataTestId="device-table-filter-network-type"
          label="Network Type"
          onChange={(selected) =>
            onChange(buildFilterState(value, { networkType: [...selected] }))
          }
          options={[...NETWORK_TYPE_OPTIONS]}
          value={[...(value.networkType ?? [])]}
        />
      )}
    </DataTableFilterContainer>
  );
}
